"use client";

/* eslint-disable @next/next/no-img-element, @next/next/no-html-link-for-pages */
import Link from "next/link";
import { usePathname } from "next/navigation";
import { carevincoAboutContent } from "@/modules/landing/content/carevincoInformation";
import { getAccountNavigation } from "@/shared/config/accountNavigation";
import {
  customerSupportEmail,
  customerSupportEmailHref,
  customerSupportPhone,
  customerSupportTelHref,
  customerSupportWhatsApp,
  customerSupportWhatsAppHref,
  isSiteActive,
} from "@/shared/config/customerSupport";
import { seoLocality } from "@/shared/config/seo";
import type { AppRole } from "@/shared/store/authStore";

type StoreLinkProps = {
  googlePlayUrl: string;
  appStoreUrl: string;
};

type FooterOptions = {
  providerRegistrationIncomplete?: boolean;
  providerVerified?: boolean;
  role?: AppRole | null;
  showInquiry?: boolean;
};

export type WireframeFooterProps = StoreLinkProps & FooterOptions;

const accountFooterHeadings: Record<AppRole, string> = {
  admin: "Admin Portal",
  provider: "Provider Portal",
  super_admin: "Super Admin Portal",
  user: "Care Requester Portal",
};

const impactMetricIcons = [
  "/assets/landingPage/wireframe-static-sections-23f9bce6-1bc7-417c-88ed-5c8a2c7869de.svg",
  "/assets/landingPage/wireframe-static-sections-e2e1e70a-df54-47b6-84c2-d5d08126b455.svg",
  "/assets/landingPage/wireframe-static-sections-e859b68e-9985-457f-ba07-419abe572d64.svg",
  "/assets/landingPage/wireframe-static-sections-d3e583c4-6dd2-4dbc-bb29-eac65af5db64.svg",
  "/assets/landingPage/wireframe-static-sections-9716e4c4-8059-4da0-af75-71436e57559b.svg",
] as const;

function ImpactContent({
  asPageHeading = false,
  useHomeCopy = false,
}: {
  asPageHeading?: boolean;
  useHomeCopy?: boolean;
}) {
  const { description, eyebrow, homeDescription, metrics, titleAccent, titleLead } = carevincoAboutContent.impact;

  return (
    <>
      <div className="section-title section-title--center">
        <p>{eyebrow}</p>
        {asPageHeading ? (
          <h1>
            {titleLead}
            <span>{titleAccent}</span>
          </h1>
        ) : (
          <h2>
            {titleLead}
            <span>{titleAccent}</span>
          </h2>
        )}
        <small>{useHomeCopy ? homeDescription : description}</small>
      </div>
      <div className="metric-grid">
        {metrics.map((metric, index) => (
          <article key={metric.title}>
            <img alt="" src={impactMetricIcons[index]} loading="lazy" decoding="async" />
            <b>{metric.value}</b>
            <h3>{metric.title}</h3>
            <p>{useHomeCopy && "homeDescription" in metric ? metric.homeDescription : metric.description}</p>
          </article>
        ))}
      </div>
    </>
  );
}

function WhyContent({ useHomeCopy = false }: { useHomeCopy?: boolean }) {
  const { description, eyebrow, homeDescription, titleAccent, titleLead } = carevincoAboutContent.why;

  return (
    <>
      <div className="mini-title">
        <img alt="" src="/assets/landingPage/wireframe-static-sections-b7d73a51-6728-4d9c-b799-249ec0632da1.svg" loading="lazy" decoding="async" />
        {` ${eyebrow} `}
      </div>
      <h2>
        <span>{titleAccent}</span>
        {titleLead}
      </h2>
      <p>{useHomeCopy ? homeDescription : description}</p>
    </>
  );
}

function isAccountFooterLinkActive(pathname: string, href: string) {
  const normalizedPathname = pathname.replace(/\/+$/, "") || "/";
  const normalizedHref = href.replace(/\/+$/, "") || "/";

  if (
    normalizedHref === "/user/bookings" &&
    normalizedPathname === "/user/tracking"
  ) {
    return true;
  }

  if (normalizedHref === "/admin/bookings" && normalizedPathname === "/admin/booking/details") {
    return true;
  }

  return normalizedPathname === normalizedHref || normalizedPathname.startsWith(`${normalizedHref}/`);
}

function ImpactDecoration() {
  return (
    <div aria-hidden="true" className="impact__decor">
      <img alt="" className="floral floral--left" src="/assets/landingPage/floral-left-e5be3fb3-449b-4d33-9697-0db531d61df8.svg" loading="lazy" decoding="async" />
      <img alt="" className="floral floral--right" src="/assets/landingPage/floral-left-e5be3fb3-449b-4d33-9697-0db531d61df8.svg" loading="lazy" decoding="async" />
      <img alt="" className="impact__wave" src="/assets/shared/impact-wave-f87a3454-2945-41c5-9595-4b931e8a9a08.svg" loading="lazy" decoding="async" />
    </div>
  );
}

export function WireframeHome({ googlePlayUrl, appStoreUrl }: StoreLinkProps) {
  return (
    <main>
      <section className="hero" id="home">
        <div className="hero__wash" />
        <img alt="Nurse speaking with an elderly couple at home" className="hero__image" src="/assets/landingPage/hero-image-cad4d553-1eb9-49d2-bb6f-06cfa8bc6d47.svg" decoding="async" />
        <div className="hero__overlay" />
        <div className="container hero__content">
          <div className="eyebrow-pill">
            <img alt="" src="/assets/landingPage/trust-icons-secure-c95c313b-3e05-4833-84b7-578b5e78e761.svg" loading="lazy" decoding="async" />
            {` Trusted by Families in ${seoLocality.displayName} `}
          </div>
          <h1>
            {" Care that feels like "}
            <span>
              {"family,"}
            </span>
            {" even when you're away. "}
          </h1>
          <p className="hero__copy">
            {" Carevinco connects families with verified home nurses to deliver compassionate, personalized care at home. Stay informed with transparent updates and enjoy complete peace of mind. "}
          </p>
          <div className="button-row">
            {isSiteActive ? (
              <a className="btn btn--outline" href={customerSupportTelHref}>
                <img alt="" src="/assets/shared/phone-63ec14fc-bcd9-4f7b-9b8b-a4200754fff0.svg" loading="lazy" decoding="async" />
                {" Talk to Care Expert"}
              </a>
            ) : null}
            <button className="btn btn--primary" data-open-inquiry="true" type="button">
              <img alt="" src="/assets/shared/calendar-0b985be5-c913-49e3-ac9c-222dc65df616.svg" loading="lazy" decoding="async" />
              {" Book a Care Visit"}
            </button>
          </div>
          <div className="rating-row">
            <div className="avatar-stack">
              <img alt="Customer" src="/assets/landingPage/customer-b39f242b-c798-4815-992b-8760a76388f2.svg" loading="lazy" decoding="async" />
              <img alt="Customer" src="/assets/landingPage/customer-19607ed2-29b1-446e-a5d3-cc01829a87b7.svg" loading="lazy" decoding="async" />
              <img alt="Customer" src="/assets/landingPage/customer-1fc13cd3-c74a-45bd-befe-e3fd4c3164af.svg" loading="lazy" decoding="async" />
              <img alt="Customer" src="/assets/landingPage/customer-2b013569-1d70-4d9e-ad24-80fd201ddd90.svg" loading="lazy" decoding="async" />
              <img alt="Customer" src="/assets/landingPage/customer-b7dcdbba-6103-4b4b-b5b2-17ebf8146928.svg" loading="lazy" decoding="async" />
            </div>
            <strong>
              {"4.9 /5"}
            </strong>
            <img alt="star" className="star" src="/assets/landingPage/star-4b296afb-75d8-4fc1-a52d-b6420f6f6481.svg" loading="lazy" decoding="async" />
            <span />
            <p>
              {"Trusted by 5000 Families"}
            </p>
          </div>
        </div>
      </section>
      <section id="how-it-works" aria-label="Home nursing in 4 simple steps" className="steps-card">
        <div className="steps-card__label">
          <span>
            {"Home Nursing in "}
          </span>
          <strong>
            {"4"}
          </strong>
          <span>
            {" Simple Steps"}
          </span>
        </div>
        <div className="steps-card__grid">
          <article className="step-item step-blue">
            <b>
              {"01"}
            </b>
            <div>
              <small>
                {"Step 01"}
              </small>
              <p>
                {"Tell Us What You Need"}
              </p>
            </div>
          </article>
          <article className="step-item step-orange">
            <b>
              {"02"}
            </b>
            <div>
              <small>
                {"Step 02"}
              </small>
              <p>
                {"Choose Your Nurse"}
              </p>
            </div>
          </article>
          <article className="step-item step-green">
            <b>
              {"03"}
            </b>
            <div>
              <small>
                {"Step 03"}
              </small>
              <p>
                {"Care Begins at Home"}
              </p>
            </div>
          </article>
          <article className="step-item step-purple">
            <b>
              {"04"}
            </b>
            <div>
              <small>
                {"Step 04"}
              </small>
              <p>
                {"Stay Updated"}
              </p>
            </div>
          </article>
        </div>
      </section>
      <section className="impact section-pad" id="about">
        <div className="container">
          <ImpactContent useHomeCopy />
        </div>
        <ImpactDecoration />
      </section>
      <section className="why why--home" id="whyCarevinco">
        <div className="container why__layout">
          <div className="why__copy">
            <WhyContent useHomeCopy />
            <button className="btn btn--primary" data-open-inquiry="true" type="button">
              <img alt="" src="/assets/landingPage/wireframe-static-sections-6501a66d-9835-4cfe-9ab1-74be97b6d944.svg" loading="lazy" decoding="async" />
              {" Book a Care Visit"}
            </button>
            <div className="trust-icons">
              <span>
                <img alt="" className="trust-icons__secure" src="/assets/landingPage/trust-icons-secure-c95c313b-3e05-4833-84b7-578b5e78e761.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"Secure"}
                  </strong>
                  <em>
                    {"& Private"}
                  </em>
                </span>
              </span>
              <span>
                <img alt="" className="trust-icons__shield" src="/assets/landingPage/trust-icons-shield-da178b9c-1182-40ad-a37f-1c1b1eaa69bf.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"100% Trusted"}
                  </strong>
                  <em>
                    {"Providers"}
                  </em>
                </span>
              </span>
            </div>
          </div>
          <div aria-label="Why families choose Carevinco" className="feature-wheel">
            <div className="feature-main">
              <img alt="Nurse and elderly couple" src="/assets/shared/why-nurse-86624d5c-9fe3-4ddd-a7a6-8785219da2a8.svg" loading="lazy" decoding="async" />
            </div>
            <article className="feature-item f1">
              <span className="feature-icon icon-verified">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-cb4cfd9f-9d8c-47dd-b8da-611ff90ea62c.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-169570c7-7bb0-4651-9148-c0c405dab04b.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-96633a2a-6fc6-464c-8f1d-d56f9fc58485.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-0555c805-53c6-41a3-9ca7-89a1894e5dfd.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Verified Home Nurses"}
                </h3>
                <p>
                  {" Every nurse is verified, qualified and background checked for your safety. "}
                </p>
              </div>
            </article>
            <article className="feature-item f2">
              <span className="feature-icon icon-professional">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-1e8c649c-a48c-40b6-a5f6-b6bc00768e71.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-0b83d120-8140-426b-97d4-7b5f98228b65.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-363e1742-1376-429d-9053-aa668dbc728f.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-16bd743c-bb1f-448c-85e4-1bbc07f25965.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Professional Care at Home"}
                </h3>
                <p>
                  {" Expert nursing care delivered safely in the comfort of your home. "}
                </p>
              </div>
            </article>
            <article className="feature-item f3">
              <span className="feature-icon icon-dedicated">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-542827c4-4671-4431-955b-fdb55d69957d.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-8b0d3b9d-e680-4f61-80c2-d6ea187be321.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-5ffc5da9-19eb-4bbf-87a2-d4df21986c9b.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-fac9e9c1-4d20-4bbb-b326-28c8a28c90b7.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Dedicated Care Coordination"}
                </h3>
                <p>
                  {" Our care team supports scheduling and answers your questions. "}
                </p>
              </div>
            </article>
            <article className="feature-item f4">
              <span className="feature-icon icon-personalized">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-542827c4-4671-4431-955b-fdb55d69957d.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-8b0d3b9d-e680-4f61-80c2-d6ea187be321.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-683a8bfe-9d1b-4402-8ecf-d3a792af37d7.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-c11ffb3b-9f37-45b4-b02c-6a7cf31db06c.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Personalized Care Plans"}
                </h3>
                <p>
                  {" Care tailored to your health needs, recovery and daily routine. "}
                </p>
              </div>
            </article>
            <article className="feature-item f5">
              <span className="feature-icon icon-updates">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-5c558d69-4838-4bbb-9426-193edc4245f0.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-ae19e987-e4b2-45ed-a6a0-0352b2a5e30f.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-f174e1e2-fb0c-4be8-abed-32d32c04ead8.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-c23a8d86-202c-4bd1-be4d-648dd71d8987.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Real-Time Family Updates"}
                </h3>
                <p>
                  {" Get visit updates, care notes and progress reports anytime. "}
                </p>
              </div>
            </article>
            <article className="feature-item f6">
              <span className="feature-icon icon-transparent">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-fc76a078-5731-4b0e-b64a-69bfc4a1becf.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-578cd1bf-dfcb-4b6c-95a4-dd77406a7b3e.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-d843d90f-0942-4ff1-a7b6-0e3dcafde461.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-f6ab6d9a-0b89-4291-971b-c403dcd426f5.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Trusted & Transparent"}
                </h3>
                <p>
                  {" Clear communication and reliable care you can trust every day. "}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
      <section className="standards section-pad" id="providers">
        <div className="container standards__layout">
          <div className="standards__copy">
            <div className="section-title">
              <p>
                {"The professionals behind every visit"}
              </p>
              <h2>
                {"Carevinco Care "}
                <span>
                  {"Standards"}
                </span>
              </h2>
              <small>
                {"Every home visit is delivered by trained, verified professionals following standardized care protocols."}
              </small>
            </div>
            <ul className="standard-list">
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-7d340384-1baa-4f93-93f4-31a47d0e6cc8.svg" loading="lazy" decoding="async" />
                <span>
                  {"Safety"}
                </span>
                <b>
                  {"Background Verified Nurses"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Quality"}
                </span>
                <b>
                  {"NABH-Inspired Care Standards"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Consistency"}
                </span>
                <b>
                  {"Regular Quality Reviews"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Transparency"}
                </span>
                <b>
                  {"Family Updates"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Support"}
                </span>
                <b>
                  {"Dedicated Care Coordinator"}
                </b>
              </li>
            </ul>
          </div>
          <div className="standards__visual">
            <img alt="" className="standards__bg" src="/assets/landingPage/standards-bg-b0d6ec7f-a510-4aac-a937-1d5c4954091a.svg" loading="lazy" decoding="async" />
            <img alt="Carevinco nurse" className="standards__nurse" src="/assets/landingPage/standards-nurse-02419400-3e61-4de4-adda-fb6b6e6191a2.svg" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>
      <section className="proof section-pad">
        <div className="container">
          <div className="section-title section-title--center">
            <p>
              {"Families Trust Carevinco"}
            </p>
            <h2>
              {"Professional Care. "}
              <span>
                {"Proven"}
              </span>
              {" Every Day."}
            </h2>
            <small>
              {"Every visit follows a consistent standard of safety, compassion, and professional care\u2014so your family always knows what to expect."}
            </small>
          </div>
          <div className="proof-card">
            <article className="story-card">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Families Love Carevinco "}
              </div>
              <h3>
                {"Real Stories. Real Impact."}
              </h3>
              <blockquote>
                {" Carevinco helped us find the right nurse for my mother. The updates and transparency gave our family complete peace of mind. "}
                <p>
                  <br />
                  <br />
                  {"\u2014 Priya S."}
                </p>
              </blockquote>
            </article>
            <article className="proof-list">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Families Trust Carevinco "}
              </div>
              <ul>
                <li>
                  {"Verified & Background-Checked Care Professionals"}
                </li>
                <li>
                  {"Personalized Care Plans Tailored To Individual Needs"}
                </li>
                <li>
                  {"Real-Time Visit Updates & Care Reports"}
                </li>
                <li>
                  {"Transparent, Secure & Reliable Care Delivery"}
                </li>
                <li>
                  {"Dedicated Care Coordination Team Available 24/7"}
                </li>
              </ul>
              <a href="#about">
                {"Learn More About Carevinco \u2192"}
              </a>
            </article>
            <article className="consult-card">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Your loved ones deserve trusted care. "}
              </div>
              <h3>
                {"Ready To Bring Peace Of Mind Home?"}
              </h3>
              <p>
                {" From elder care and home nursing to personalized support and wellness services, Carevinco helps families stay connected, informed and confident. "}
              </p>
              <strong>
                {"Request A Free Consultation"}
              </strong>
              {isSiteActive ? (
                <a className="btn btn--orange" href={customerSupportTelHref}>
                  <img alt="" src="/assets/landingPage/wireframe-static-sections-9d756754-1323-4a78-a01e-223bebdcc03c.svg" loading="lazy" decoding="async" />
                  {` Call Our Care Team : ${customerSupportPhone}`}
                </a>
              ) : null}
            </article>
          </div>
        </div>
      </section>
      <section className="app-section" id="downloads">
        <div className="container app-section__layout">
          <div className="phone-visual">
            <img alt="Carevinco mobile app preview" className="phone-visual__image" src="/assets/landingPage/phone-visual-image-c212cc76-1345-4b19-a3b4-d7381b55b314.svg" loading="lazy" decoding="async" />
          </div>
          <div className="app-copy">
            <h2>
              {" Stay Close, No Matter The Distance."}
              <br />
              <span>
                {"Take Care,"}
              </span>
              {" Anywhere You Are "}
            </h2>
            <p>
              {" Download the Carevinco app and manage care with confidence, transparency and complete peace of mind. "}
            </p>
            <p>
              {" The Carevinco app helps you stay connected with your loved ones, manage care effortlessly and receive real-time updates with complete peace of mind. "}
            </p>
            <div className="store-row">
              <a aria-label="Download Carevinco on Google Play" href={googlePlayUrl} rel="noopener noreferrer" target="_blank">
                <img alt="Get it on Google Play" src="/assets/shared/play-store-5bfae79b-7b48-4651-b91c-a45a2a97b1b6.svg" loading="lazy" decoding="async" />
              </a>
              <a aria-label="Download Carevinco on the App Store" href={appStoreUrl} rel="noopener noreferrer" target="_blank">
                <img alt="Download on App Store" src="/assets/shared/app-store-8dcf9a3c-c0d6-4941-9e33-4697e5ccc5e3.svg" loading="lazy" decoding="async" />
              </a>
            </div>
            <div className="app-benefits">
              <span>
                <img alt="" className="app-benefits__secure" src="/assets/landingPage/app-benefits-secure-46d44811-30af-47db-ac3c-6ac88bf80bec.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"Secure"}
                  </strong>
                  <em>
                    {"& Private"}
                  </em>
                </span>
              </span>
              <span>
                <img alt="" className="app-benefits__shield" src="/assets/landingPage/app-benefits-shield-ea3d95cf-5683-4b54-a03d-7eb364a06275.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"100% Trusted"}
                  </strong>
                  <em>
                    {"Providers"}
                  </em>
                </span>
              </span>
              <span>
                <img alt="" className="app-benefits__support" src="/assets/landingPage/app-benefits-support-423909e6-26c5-4b80-889b-7555b96e688e.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"24/7 Care"}
                  </strong>
                  <em>
                    {"Support"}
                  </em>
                </span>
              </span>
            </div>
          </div>
        </div>
      </section>
      <section className="faq section-pad" id="faq">
        <div className="container faq__layout">
          <div className="section-title section-title--center">
            <p>
              {"Frequently Asked Questions"}
            </p>
            <h2>
              {"Carevinco Premium FAQs"}
            </h2>
          </div>
          <div className="accordion" data-accordion="">
            <article className="faq-item is-open">
              <button type="button">
                {"What is Carevinco?"}
                <span />
              </button>
              <p>
                {" Carevinco is a care management platform that helps families connect with verified nurses, doctors and wellness professionals while staying informed throughout the care journey. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How do I request a care service?"}
                <span />
              </button>
              <p>
                {" You can request a service through the care visit form, phone consultation, or the family portal. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" What services does Carevinco provide?"}
                <span />
              </button>
              <p>
                {" Home nursing, elderly support, patient care, post-hospital care, wellness support and coordinated care services. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Are all care providers verified?"}
                <span />
              </button>
              <p>
                {" Yes. Care professionals are identity-checked, qualification-reviewed, and background-verified. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I choose my preferred nurse?"}
                <span />
              </button>
              <p>
                {" Yes. Families will be assigned a suitable nurse based on thrir preffered care needs, gender, languages and availability. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How will I know what happens during a visit?"}
                <span />
              </button>
              <p>
                {" You receive visit updates, care notes and coordination support throughout the care journey. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I book care services for my parents while living abroad?"}
                <span />
              </button>
              <p>
                {" Yes. Carevinco is designed to support families managing care remotely. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" What happens if I am not satisfied with a provider?"}
                <span />
              </button>
              <p>
                {" The care coordination team reviews the concern and supports reassignment or resolution. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Are the care plans personalized?"}
                <span />
              </button>
              <p>
                {" Yes. Care plans are tailored to health needs, routines and recovery requirements. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Is emergency support available?"}
                <span />
              </button>
              <p>
                {" Care support is available 24/7 for coordination and guidance. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How do payments work?"}
                <span />
              </button>
              <p>
                {" Payment workflows are handled transparently through the care coordination process. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I track appointments and schedules?"}
                <span />
              </button>
              <p>
                {" Yes. Appointments and schedules can be managed through Carevinco updates and app workflows. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Will I receive regular updates about my loved one?"}
                <span />
              </button>
              <p>
                {" Yes. Families receive regular visit updates, care notes and progress visibility. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Is my personal information secure?"}
                <span />
              </button>
              <p>
                {" Carevinco follows secure handling practices for family and care information. "}
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}

export function WireframeFooter({
  googlePlayUrl,
  appStoreUrl,
  providerRegistrationIncomplete = false,
  providerVerified = false,
  role,
  showInquiry = true,
}: WireframeFooterProps) {
  const pathname = usePathname();
  const accountLinks = getAccountNavigation(role, {
    providerVerified: !providerRegistrationIncomplete && providerVerified,
  });
  const isAccountFooter = accountLinks.length > 0;
  const footerClassName = `footer${isAccountFooter ? " footer--account" : ""}${isAccountFooter && !showInquiry ? " footer--compact" : ""}`;

  return (
    <footer className={footerClassName} id="contact">
      {showInquiry ? (
        <div className="container footer__grid">
          <section className="footer-brand">
            <a className="brand brand--footer" href="/#home">
              <img alt="Carevinco mark" className="brand__mark" src="/assets/brand/brand-mark-1cca4780-46f9-4578-93eb-955b77bb999c.svg" loading="lazy" decoding="async" />
              <span className="brand__typewrap">
                <img alt="Carevinco" className="brand__type" src="/assets/brand/brand-type-25cfdaf2-54d7-476a-8af1-227ff117d06b.svg" loading="lazy" decoding="async" />
                <small>
                  {"Empowering Lives with Compassionate Support"}
                </small>
              </span>
            </a>
            <div aria-hidden="true" className="social-row">
              <img alt="" src="/assets/landingPage/facebook-245f2149-02cf-4a7c-bc8d-2793a32cadd6.svg" loading="lazy" decoding="async" />
              <img alt="" src="/assets/landingPage/instagram-96325ffc-1bb7-484f-9489-f5b46a89e83c.svg" loading="lazy" decoding="async" />
              <img alt="" src="/assets/landingPage/you-tube-7c9449af-dad8-4e9a-b1dd-ed29f02484ac.svg" loading="lazy" decoding="async" />
              <img alt="" src="/assets/landingPage/linked-in-b654d211-453b-4490-a861-8265d1d0bd8c.svg" loading="lazy" decoding="async" />
              <img alt="" src="/assets/landingPage/twitter-0e343ca4-b03f-4607-94a9-e1d04357efcd.svg" loading="lazy" decoding="async" />
              {isSiteActive ? <img alt="" src="/assets/landingPage/whats-app-dbf5a2cf-627d-4544-bc68-f2388a3f1573.svg" loading="lazy" decoding="async" /> : null}
            </div>
            <p>
              {` Trusted home nursing in ${seoLocality.displayName}, delivering compassionate care with confidence and peace of mind. `}
            </p>
          </section>
          {isAccountFooter ? (
            <section className="footer-account-nav">
              <h3>{role ? accountFooterHeadings[role] : "Account"}</h3>
              {accountLinks.map((link) => {
                const active = isAccountFooterLinkActive(pathname, link.href);

                return (
                  <Link
                    aria-current={active ? "page" : undefined}
                    className={active ? "is-active" : undefined}
                    href={link.href}
                    key={link.href}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </section>
          ) : null}
          <section>
            <h3>
              {"Support"}
            </h3>
            {isAccountFooter ? (
              <>
                <Link href="/contact-us">Help &amp; Contact</Link>
                {isSiteActive ? <a href={customerSupportTelHref}>Call Customer Care</a> : null}
                {isSiteActive ? (
                  <a href={customerSupportWhatsAppHref} rel="noopener noreferrer" target="_blank">
                    WhatsApp Support
                  </a>
                ) : null}
                <a href={customerSupportEmailHref}>Email Support</a>
              </>
            ) : (
              <Link href="/contact-us">Help Center</Link>
            )}
          </section>
          <section className="contact-block">
            <h3>
              {"Get In Touch"}
            </h3>
            {isSiteActive ? (
              <>
                <b>{customerSupportPhone}</b>
                <span>{"Customer Care"}</span>
                <b>{customerSupportWhatsApp}</b>
                <span>{"WhatsApp Support"}</span>
              </>
            ) : null}
            <a className="mail" href={customerSupportEmailHref}>
              {customerSupportEmail}
            </a>
            <span>
              {"Email"}
            </span>
          </section>
        </div>
      ) : null}
      {showInquiry ? (
        <div className="container service-row">
          <section>
            <h3>
              {"Service Areas"}
            </h3>
            <div className="chips">
              <b>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-ec990d8c-70d0-4676-8ba0-42f6ae644dce.svg" loading="lazy" decoding="async" />
                {` ${seoLocality.displayName}`}
              </b>
            </div>
            <Link className="text-link" href="/services">
              {"View Nursing Services \u2192"}
            </Link>
          </section>
          <section className="footer-cta">
            <h3>
              {"Need Trusted Home Nursing Care For Your Loved One ?"}
            </h3>
            <p>
              {"Professional nursing care is just a few clicks away."}
            </p>
            <div className="button-row">
              {isSiteActive ? (
                <a className="btn btn--outline" href={customerSupportTelHref}>
                  <img alt="" src="/assets/shared/phone-63ec14fc-bcd9-4f7b-9b8b-a4200754fff0.svg" loading="lazy" decoding="async" />
                  {" Talk to Care Expert"}
                </a>
              ) : null}
              <button className="btn btn--orange" data-open-inquiry="true" type="button">
                <img alt="" src="/assets/shared/calendar-0b985be5-c913-49e3-ac9c-222dc65df616.svg" loading="lazy" decoding="async" />
                {" Book a Care Visit"}
              </button>
            </div>
            <p>
              {" Download the Carevinco app and manage care with confidence, transparency and complete peace of mind. "}
            </p>
            <div className="store-row store-row--small">
              <a aria-label="Download Carevinco on Google Play" href={googlePlayUrl} rel="noopener noreferrer" target="_blank">
                <img alt="Get it on Google Play" src="/assets/landingPage/get-it-on-google-play-6f3745f5-3193-435f-86ee-3a52e9f79847.svg" loading="lazy" decoding="async" />
              </a>
              <a aria-label="Download Carevinco on the App Store" href={appStoreUrl} rel="noopener noreferrer" target="_blank">
                <img alt="Download on App Store" src="/assets/landingPage/download-on-app-store-59f980cd-a91a-4a77-b798-9cae570c83d4.svg" loading="lazy" decoding="async" />
              </a>
            </div>
          </section>
        </div>
      ) : null}
      {showInquiry ? (
        <div className="container promise">
          <b>
            {"Our Promise : Empowering lives with compassionate support."}
          </b>
          <p>
            {` Home Nursing Services | Home Nursing in ${seoLocality.displayName} | Home Nurse | Nursing Care at Home | Elderly Nursing Care | Patient Care at Home | Professional Home Nurses | Home Healthcare | Nursing Services | Post-Hospital Care | Palliative Nursing Care | Home Care Services | Verified Home Nurses `}
          </p>
        </div>
      ) : null}
      <div className="copyright">
        <div className="container">
          {"\u00a9 2026 Carevinco. All rights reserved."}
        </div>
      </div>
    </footer>
  );
}

export function WireframeAbout() {
  return (
    <main id="about-page">
      <section className="impact section-pad" id="about">
        <div className="container">
          <ImpactContent asPageHeading />
        </div>
        <ImpactDecoration />
      </section>
      <section className="why" id="whyCarevinco">
        <div className="container why__layout">
          <div className="why__copy">
            <WhyContent />
            <button className="btn btn--primary" data-open-inquiry="true" type="button">
              <img alt="" src="/assets/landingPage/wireframe-static-sections-6501a66d-9835-4cfe-9ab1-74be97b6d944.svg" loading="lazy" decoding="async" />
              {" Book a Care Visit"}
            </button>
            <div className="trust-icons">
              <span>
                <img alt="" className="trust-icons__secure" src="/assets/landingPage/trust-icons-secure-c95c313b-3e05-4833-84b7-578b5e78e761.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"Secure"}
                  </strong>
                  <em>
                    {"& Private"}
                  </em>
                </span>
              </span>
              <span>
                <img alt="" className="trust-icons__shield" src="/assets/landingPage/trust-icons-shield-da178b9c-1182-40ad-a37f-1c1b1eaa69bf.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"100% Trusted"}
                  </strong>
                  <em>
                    {"Providers"}
                  </em>
                </span>
              </span>
            </div>
          </div>
          <div aria-label="Why families choose Carevinco" className="feature-wheel">
            <div className="feature-main">
              <img alt="Nurse and elderly couple" src="/assets/shared/why-nurse-86624d5c-9fe3-4ddd-a7a6-8785219da2a8.svg" loading="lazy" decoding="async" />
            </div>
            <article className="feature-item f1">
              <span className="feature-icon icon-verified">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-cb4cfd9f-9d8c-47dd-b8da-611ff90ea62c.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-169570c7-7bb0-4651-9148-c0c405dab04b.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-96633a2a-6fc6-464c-8f1d-d56f9fc58485.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-0555c805-53c6-41a3-9ca7-89a1894e5dfd.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Verified Home Nurses"}
                </h3>
                <p>
                  {" Every nurse is verified, qualified, and background checked for your safety. "}
                </p>
              </div>
            </article>
            <article className="feature-item f2">
              <span className="feature-icon icon-professional">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-1e8c649c-a48c-40b6-a5f6-b6bc00768e71.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-0b83d120-8140-426b-97d4-7b5f98228b65.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-363e1742-1376-429d-9053-aa668dbc728f.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-16bd743c-bb1f-448c-85e4-1bbc07f25965.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Professional Care at Home"}
                </h3>
                <p>
                  {" Expert nursing care delivered safely in the comfort of your home. "}
                </p>
              </div>
            </article>
            <article className="feature-item f3">
              <span className="feature-icon icon-dedicated">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-542827c4-4671-4431-955b-fdb55d69957d.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-8b0d3b9d-e680-4f61-80c2-d6ea187be321.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-5ffc5da9-19eb-4bbf-87a2-d4df21986c9b.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-fac9e9c1-4d20-4bbb-b326-28c8a28c90b7.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Dedicated Care Coordination"}
                </h3>
                <p>
                  {" Our care team supports scheduling and answers your questions. "}
                </p>
              </div>
            </article>
            <article className="feature-item f4">
              <span className="feature-icon icon-personalized">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-542827c4-4671-4431-955b-fdb55d69957d.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-8b0d3b9d-e680-4f61-80c2-d6ea187be321.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-683a8bfe-9d1b-4402-8ecf-d3a792af37d7.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-c11ffb3b-9f37-45b4-b02c-6a7cf31db06c.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Personalized Care Plans"}
                </h3>
                <p>
                  {" Care tailored to your health needs, recovery, and daily routine. "}
                </p>
              </div>
            </article>
            <article className="feature-item f5">
              <span className="feature-icon icon-updates">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-5c558d69-4838-4bbb-9426-193edc4245f0.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-ae19e987-e4b2-45ed-a6a0-0352b2a5e30f.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-f174e1e2-fb0c-4be8-abed-32d32c04ead8.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-c23a8d86-202c-4bd1-be4d-648dd71d8987.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Real-Time Family Updates"}
                </h3>
                <p>
                  {" Get visit updates, care notes, and progress reports anytime. "}
                </p>
              </div>
            </article>
            <article className="feature-item f6">
              <span className="feature-icon icon-transparent">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-fc76a078-5731-4b0e-b64a-69bfc4a1becf.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-578cd1bf-dfcb-4b6c-95a4-dd77406a7b3e.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-d843d90f-0942-4ff1-a7b6-0e3dcafde461.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-f6ab6d9a-0b89-4291-971b-c403dcd426f5.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Trusted & Transparent"}
                </h3>
                <p>
                  {" Clear communication and reliable care you can trust every day. "}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
      <section className="standards section-pad" id="providers">
        <div className="container standards__layout">
          <div className="standards__copy">
            <div className="section-title">
              <p>
                {"The professionals behind every visit"}
              </p>
              <h2>
                {"Carevinco Care "}
                <span>
                  {"Standards"}
                </span>
              </h2>
              <small>
                {"Every home visit is delivered by trained, verified professionals following standardized care protocols."}
              </small>
            </div>
            <ul className="standard-list">
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-7d340384-1baa-4f93-93f4-31a47d0e6cc8.svg" loading="lazy" decoding="async" />
                <span>
                  {"Safety"}
                </span>
                <b>
                  {"Background Verified Nurses"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Quality"}
                </span>
                <b>
                  {"NABH-Inspired Care Standards"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Consistency"}
                </span>
                <b>
                  {"Regular Quality Reviews"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Transparency"}
                </span>
                <b>
                  {"Family Updates"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Support"}
                </span>
                <b>
                  {"Dedicated Care Coordinator"}
                </b>
              </li>
            </ul>
          </div>
          <div className="standards__visual">
            <img alt="" className="standards__bg" src="/assets/landingPage/standards-bg-b0d6ec7f-a510-4aac-a937-1d5c4954091a.svg" loading="lazy" decoding="async" />
            <img alt="Carevinco nurse" className="standards__nurse" src="/assets/landingPage/standards-nurse-02419400-3e61-4de4-adda-fb6b6e6191a2.svg" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>
      <section className="proof section-pad">
        <div className="container">
          <div className="section-title section-title--center">
            <p>
              {"Families Trust Carevinco"}
            </p>
            <h2>
              {"Professional Care. "}
              <span>
                {"Proven"}
              </span>
              {" Every Day."}
            </h2>
            <small>
              {"Every visit follows a consistent standard of safety, compassion, and professional care\u2014so your family always knows what to expect."}
            </small>
          </div>
          <div className="proof-card">
            <article className="story-card">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Families Love Carevinco "}
              </div>
              <h3>
                {"Real Stories. Real Impact."}
              </h3>
              <blockquote>
                {" Carevinco helped us find the right nurse for my mother. The updates and transparency gave our family complete peace of mind. "}
                <p>
                  <br />
                  <br />
                  {"\u2014 Priya S."}
                </p>
              </blockquote>
            </article>
            <article className="proof-list">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Families Trust Carevinco "}
              </div>
              <ul>
                <li>
                  {"Verified & Background-Checked Care Professionals"}
                </li>
                <li>
                  {"Personalized Care Plans Tailored To Individual Needs"}
                </li>
                <li>
                  {"Real-Time Visit Updates & Care Reports"}
                </li>
                <li>
                  {"Transparent, Secure & Reliable Care Delivery"}
                </li>
                <li>
                  {"Dedicated Care Coordination Team Available 24/7"}
                </li>
              </ul>
              <a href="#about">
                {"Learn More About Carevinco \u2192"}
              </a>
            </article>
            <article className="consult-card">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Your loved ones deserve trusted care. "}
              </div>
              <h3>
                {"Ready To Bring Peace Of Mind Home?"}
              </h3>
              <p>
                {" From elder care and home nursing to personalized support and wellness services, Carevinco helps families stay connected, informed, and confident. "}
              </p>
              <strong>
                {"Request A Free Consultation"}
              </strong>
              {isSiteActive ? (
                <a className="btn btn--orange" href={customerSupportTelHref}>
                  <img alt="" src="/assets/landingPage/wireframe-static-sections-9d756754-1323-4a78-a01e-223bebdcc03c.svg" loading="lazy" decoding="async" />
                  {` Call Our Care Team : ${customerSupportPhone}`}
                </a>
              ) : null}
            </article>
          </div>
        </div>
      </section>
      <section className="faq section-pad" id="faq">
        <div className="container faq__layout">
          <div className="section-title section-title--center">
            <p>
              {"Frequently Asked Questions"}
            </p>
            <h2>
              {"Carevinco Premium FAQs"}
            </h2>
          </div>
          <div className="accordion" data-accordion="">
            <article className="faq-item is-open">
              <button type="button">
                {"What is Carevinco?"}
                <span />
              </button>
              <p>
                {" Carevinco is a care management platform that helps families connect with verified caregivers, nurses, doctors, and wellness professionals while staying informed throughout the care journey. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How do I request a care service?"}
                <span />
              </button>
              <p>
                {" You can request a service through the care visit form, phone consultation, or the family portal. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" What services does Carevinco provide?"}
                <span />
              </button>
              <p>
                {" Home nursing, elderly support, patient care, post-hospital care, wellness support, and coordinated care services. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Are all care providers verified?"}
                <span />
              </button>
              <p>
                {" Yes. Care professionals are identity-checked, qualification-reviewed, and background-verified. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I choose my preferred nurse?"}
                <span />
              </button>
              <p>
                {" Yes. Families can get a preferred nurse based on gender and language preferences, ensuring a good match for your family's needs. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How will I know what happens during a visit?"}
                <span />
              </button>
              <p>
                {" You receive visit updates, care notes, and coordination support throughout the care journey. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I book care services for my parents while living abroad?"}
                <span />
              </button>
              <p>
                {" Yes. Carevinco is designed to support families managing care remotely. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" What happens if I am not satisfied with a provider?"}
                <span />
              </button>
              <p>
                {" The care coordination team reviews the concern and supports reassignment or resolution. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Are the care plans personalized?"}
                <span />
              </button>
              <p>
                {" Yes. Care plans are tailored to health needs, routines, and recovery requirements. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Is emergency support available?"}
                <span />
              </button>
              <p>
                {" Care support is available 24/7 for coordination and guidance. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How do payments work?"}
                <span />
              </button>
              <p>
                {" Payment workflows are handled transparently through the care coordination process. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I track appointments and schedules?"}
                <span />
              </button>
              <p>
                {" Yes. Appointments and schedules can be managed through Carevinco updates and app workflows. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Will I receive regular updates about my loved one?"}
                <span />
              </button>
              <p>
                {" Yes. Families receive regular visit updates, care notes, and progress visibility. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Is my personal information secure?"}
                <span />
              </button>
              <p>
                {" Carevinco follows secure handling practices for family and care information. "}
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}

export function WireframeBenefits({ googlePlayUrl, appStoreUrl }: StoreLinkProps) {
  return (
    <main id="benefits-page">
      <section className="why" id="whyCarevinco">
        <div className="container why__layout">
          <div className="why__copy">
            <WhyContent />
            <button className="btn btn--primary" data-open-inquiry="true" type="button">
              <img alt="" src="/assets/landingPage/wireframe-static-sections-6501a66d-9835-4cfe-9ab1-74be97b6d944.svg" loading="lazy" decoding="async" />
              {" Book a Care Visit"}
            </button>
            <div className="trust-icons">
              <span>
                <img alt="" className="trust-icons__secure" src="/assets/landingPage/trust-icons-secure-c95c313b-3e05-4833-84b7-578b5e78e761.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"Secure"}
                  </strong>
                  <em>
                    {"& Private"}
                  </em>
                </span>
              </span>
              <span>
                <img alt="" className="trust-icons__shield" src="/assets/landingPage/trust-icons-shield-da178b9c-1182-40ad-a37f-1c1b1eaa69bf.svg" loading="lazy" decoding="async" />
                <span>
                  <strong>
                    {"100% Trusted"}
                  </strong>
                  <em>
                    {"Providers"}
                  </em>
                </span>
              </span>
            </div>
          </div>
          <div aria-label="Why families choose Carevinco" className="feature-wheel">
            <div className="feature-main">
              <img alt="Nurse and elderly couple" src="/assets/shared/why-nurse-86624d5c-9fe3-4ddd-a7a6-8785219da2a8.svg" loading="lazy" decoding="async" />
            </div>
            <article className="feature-item f1">
              <span className="feature-icon icon-verified">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-cb4cfd9f-9d8c-47dd-b8da-611ff90ea62c.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-169570c7-7bb0-4651-9148-c0c405dab04b.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-96633a2a-6fc6-464c-8f1d-d56f9fc58485.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-0555c805-53c6-41a3-9ca7-89a1894e5dfd.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Verified Home Nurses"}
                </h3>
                <p>
                  {" Every nurse is verified, qualified, and background checked for your safety. "}
                </p>
              </div>
            </article>
            <article className="feature-item f2">
              <span className="feature-icon icon-professional">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-1e8c649c-a48c-40b6-a5f6-b6bc00768e71.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-0b83d120-8140-426b-97d4-7b5f98228b65.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-363e1742-1376-429d-9053-aa668dbc728f.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-16bd743c-bb1f-448c-85e4-1bbc07f25965.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Professional Care at Home"}
                </h3>
                <p>
                  {" Expert nursing care delivered safely in the comfort of your home. "}
                </p>
              </div>
            </article>
            <article className="feature-item f3">
              <span className="feature-icon icon-dedicated">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-542827c4-4671-4431-955b-fdb55d69957d.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-8b0d3b9d-e680-4f61-80c2-d6ea187be321.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-5ffc5da9-19eb-4bbf-87a2-d4df21986c9b.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-fac9e9c1-4d20-4bbb-b326-28c8a28c90b7.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Dedicated Care Coordination"}
                </h3>
                <p>
                  {" Our care team supports scheduling and answers your questions. "}
                </p>
              </div>
            </article>
            <article className="feature-item f4">
              <span className="feature-icon icon-personalized">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-542827c4-4671-4431-955b-fdb55d69957d.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-8b0d3b9d-e680-4f61-80c2-d6ea187be321.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-683a8bfe-9d1b-4402-8ecf-d3a792af37d7.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-c11ffb3b-9f37-45b4-b02c-6a7cf31db06c.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Personalized Care Plans"}
                </h3>
                <p>
                  {" Care tailored to your health needs, recovery, and daily routine. "}
                </p>
              </div>
            </article>
            <article className="feature-item f5">
              <span className="feature-icon icon-updates">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-5c558d69-4838-4bbb-9426-193edc4245f0.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-ae19e987-e4b2-45ed-a6a0-0352b2a5e30f.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-f174e1e2-fb0c-4be8-abed-32d32c04ead8.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-c23a8d86-202c-4bd1-be4d-648dd71d8987.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Real-Time Family Updates"}
                </h3>
                <p>
                  {" Get visit updates, care notes, and progress reports anytime. "}
                </p>
              </div>
            </article>
            <article className="feature-item f6">
              <span className="feature-icon icon-transparent">
                <img alt="" className="feature-icon__base" src="/assets/landingPage/feature-icon-base-fc76a078-5731-4b0e-b64a-69bfc4a1becf.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__halo" src="/assets/landingPage/feature-icon-halo-578cd1bf-dfcb-4b6c-95a4-dd77406a7b3e.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__art" src="/assets/landingPage/feature-icon-art-d843d90f-0942-4ff1-a7b6-0e3dcafde461.svg" loading="lazy" decoding="async" />
                <img alt="" className="feature-icon__glyph" src="/assets/landingPage/feature-icon-glyph-f6ab6d9a-0b89-4291-971b-c403dcd426f5.svg" loading="lazy" decoding="async" />
              </span>
              <div>
                <h3>
                  {"Trusted & Transparent"}
                </h3>
                <p>
                  {" Clear communication and reliable care you can trust every day. "}
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
      <section className="standards section-pad" id="providers">
        <div className="container standards__layout">
          <div className="standards__copy">
            <div className="section-title">
              <p>
                {"The professionals behind every visit"}
              </p>
              <h2>
                {"Carevinco Care "}
                <span>
                  {"Standards"}
                </span>
              </h2>
              <small>
                {"Every home visit is delivered by trained, verified professionals following standardized care protocols."}
              </small>
            </div>
            <ul className="standard-list">
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-7d340384-1baa-4f93-93f4-31a47d0e6cc8.svg" loading="lazy" decoding="async" />
                <span>
                  {"Safety"}
                </span>
                <b>
                  {"Background Verified Nurses"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Quality"}
                </span>
                <b>
                  {"NABH-Inspired Care Standards"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Consistency"}
                </span>
                <b>
                  {"Regular Quality Reviews"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Transparency"}
                </span>
                <b>
                  {"Family Updates"}
                </b>
              </li>
              <li>
                <img alt="" src="/assets/landingPage/wireframe-static-sections-f5200c74-d110-47b9-84d6-0a2b6ebe7e1b.svg" loading="lazy" decoding="async" />
                <span>
                  {"Support"}
                </span>
                <b>
                  {"Dedicated Care Coordinator"}
                </b>
              </li>
            </ul>
          </div>
          <div className="standards__visual">
            <img alt="" className="standards__bg" src="/assets/landingPage/standards-bg-b0d6ec7f-a510-4aac-a937-1d5c4954091a.svg" loading="lazy" decoding="async" />
            <img alt="Carevinco nurse" className="standards__nurse" src="/assets/landingPage/standards-nurse-02419400-3e61-4de4-adda-fb6b6e6191a2.svg" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>
      <section className="proof section-pad">
        <div className="container">
          <div className="section-title section-title--center">
            <p>
              {"Families Trust Carevinco"}
            </p>
            <h2>
              {"Professional Care. "}
              <span>
                {"Proven"}
              </span>
              {" Every Day."}
            </h2>
            <small>
              {"Every visit follows a consistent standard of safety, compassion, and professional care\u2014so your family always knows what to expect."}
            </small>
          </div>
          <div className="proof-card">
            <article className="story-card">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Families Love Carevinco "}
              </div>
              <h3>
                {"Real Stories. Real Impact."}
              </h3>
              <blockquote>
                {" Carevinco helped us find the right nurse for my mother. The updates and transparency gave our family complete peace of mind. "}
                <p>
                  <br />
                  <br />
                  {"\u2014 Priya S."}
                </p>
              </blockquote>
            </article>
            <article className="proof-list">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Families Trust Carevinco "}
              </div>
              <ul>
                <li>
                  {"Verified & Background-Checked Care Professionals"}
                </li>
                <li>
                  {"Personalized Care Plans Tailored To Individual Needs"}
                </li>
                <li>
                  {"Real-Time Visit Updates & Care Reports"}
                </li>
                <li>
                  {"Transparent, Secure & Reliable Care Delivery"}
                </li>
                <li>
                  {"Dedicated Care Coordination Team Available 24/7"}
                </li>
              </ul>
              <a href="#about">
                {"Learn More About Carevinco \u2192"}
              </a>
            </article>
            <article className="consult-card">
              <div className="mini-title">
                <img alt="" src="/assets/landingPage/wireframe-static-sections-49b48f40-85d6-43d9-b30f-93408cbc3dab.svg" loading="lazy" decoding="async" />
                {" Your loved ones deserve trusted care. "}
              </div>
              <h3>
                {"Ready To Bring Peace Of Mind Home?"}
              </h3>
              <p>
                {" From elder care and home nursing to personalized support and wellness services, Carevinco helps families stay connected, informed, and confident. "}
              </p>
              <strong>
                {"Request A Free Consultation"}
              </strong>
              {isSiteActive ? (
                <a className="btn btn--orange" href={customerSupportTelHref}>
                  <img alt="" src="/assets/landingPage/wireframe-static-sections-9d756754-1323-4a78-a01e-223bebdcc03c.svg" loading="lazy" decoding="async" />
                  {` Call Our Care Team : ${customerSupportPhone}`}
                </a>
              ) : null}
            </article>
          </div>
        </div>
      </section>
      <section className="faq section-pad" id="faq">
        <div className="container faq__layout">
          <div className="section-title section-title--center">
            <p>
              {"Frequently Asked Questions"}
            </p>
            <h2>
              {"Carevinco Premium FAQs"}
            </h2>
          </div>
          <div className="accordion" data-accordion="">
            <article className="faq-item is-open">
              <button type="button">
                {"What is Carevinco?"}
                <span />
              </button>
              <p>
                {" Carevinco is a care management platform that helps families connect with verified caregivers, nurses, doctors, and wellness professionals while staying informed throughout the care journey. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How do I request a care service?"}
                <span />
              </button>
              <p>
                {" You can request a service through the care visit form, phone consultation, or the family portal. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" What services does Carevinco provide?"}
                <span />
              </button>
              <p>
                {" Home nursing, elderly support, patient care, post-hospital care, wellness support, and coordinated care services. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Are all care providers verified?"}
                <span />
              </button>
              <p>
                {" Yes. Care professionals are identity-checked, qualification-reviewed, and background-verified. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I choose my preferred nurse?"}
                <span />
              </button>
              <p>
                {" Yes. Families can get a preferred nurse based on gender and language preferences, ensuring a good match for your family's needs. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How will I know what happens during a visit?"}
                <span />
              </button>
              <p>
                {" You receive visit updates, care notes, and coordination support throughout the care journey. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I book care services for my parents while living abroad?"}
                <span />
              </button>
              <p>
                {" Yes. Carevinco is designed to support families managing care remotely. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" What happens if I am not satisfied with a provider?"}
                <span />
              </button>
              <p>
                {" The care coordination team reviews the concern and supports reassignment or resolution. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Are the care plans personalized?"}
                <span />
              </button>
              <p>
                {" Yes. Care plans are tailored to health needs, routines, and recovery requirements. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Is emergency support available?"}
                <span />
              </button>
              <p>
                {" Care support is available 24/7 for coordination and guidance. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" How do payments work?"}
                <span />
              </button>
              <p>
                {" Payment workflows are handled transparently through the care coordination process. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Can I track appointments and schedules?"}
                <span />
              </button>
              <p>
                {" Yes. Appointments and schedules can be managed through Carevinco updates and app workflows. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Will I receive regular updates about my loved one?"}
                <span />
              </button>
              <p>
                {" Yes. Families receive regular visit updates, care notes, and progress visibility. "}
              </p>
            </article>
            <article className="faq-item">
              <button type="button">
                {" Is my personal information secure?"}
                <span />
              </button>
              <p>
                {" Carevinco follows secure handling practices for family and care information. "}
              </p>
            </article>
          </div>
        </div>
      </section>
    </main>
  );
}
